import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import './SessionsChart.css';
import tooltipTexts from '../../utils/tooltipTexts';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../api/apiClient';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const getCssVar = (name, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

  const CATEGORY_COLORS = {
  Excellent: '#43a047',
  Good: '#fbc02d',
  Average: '#fb8c00',
  Poor: '#e53935'
};

const SessionsChart = ({ vehicleId }) => {
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [viewType, setViewType] = useState('session');

  useEffect(() => {
    fetchData();
  }, [viewType, vehicleId]);

  const getBackgroundColor = (category) => CATEGORY_COLORS[category] || '#94a3b8';

  const createDataset = (category, labels, data) => {
    const color = getBackgroundColor(category);
    return {
      label: category,
      data: labels.map((label) => {
        if (data[label] && data[label][category] !== undefined) {
          return parseFloat(data[label][category] || '0');
        }
        return 0;
      }),
      backgroundColor: color,
      borderColor: color,
      borderWidth: 0,
      borderRadius: 4,
      borderSkipped: false,
      hoverBackgroundColor: color,
      barPercentage: 0.72,
      categoryPercentage: 0.68,
      maxBarThickness: 28,
      minBarLength: 2
    };
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const endpoint = viewType === 'session'
        ? '/api/score-distribution'
        : '/api/assessment-distribution';

      const url = vehicleId
        ? `${API_BASE_URL}${endpoint}?vehicleId=${vehicleId}`
        : `${API_BASE_URL}${endpoint}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();

      const labels = Object.keys(data);
      const categories = ['Excellent', 'Good', 'Average', 'Poor'];
      const datasets = categories.map((category) => createDataset(category, labels, data));

      setChartData({
        labels,
        datasets
      });
    } catch (error) {
      console.error('Error loading chart data:', error);
      setChartData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const options = useMemo(() => {
    const tickColor = getCssVar('--chart-tick', theme === 'dark' ? '#e1e1e6' : '#334155');
    const gridColor = getCssVar('--chart-grid', theme === 'dark' ? '#3a3f45' : '#e8edf3');
    const tooltipBg = getCssVar('--bg-card', theme === 'dark' ? '#2a2d31' : '#ffffff');
    const tooltipText = getCssVar('--text-body', theme === 'dark' ? '#e1e1e6' : '#1f2937');
    const tooltipMuted = getCssVar('--text-muted', theme === 'dark' ? '#a0a0a0' : '#64748b');
    const border = getCssVar('--border', theme === 'dark' ? 'rgba(255,255,255,0.12)' : '#e5e7eb');

    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 4,
          right: 6,
          bottom: 0,
          left: 2
        }
      },
      scales: {
        x: {
          stacked: false,
          grid: {
            display: false,
            drawBorder: false
          },
          ticks: {
            font: {
              size: 11,
              weight: '600'
            },
            color: tickColor,
            maxRotation: 0,
            autoSkip: true
          }
        },
        y: {
          stacked: false,
          beginAtZero: true,
          max: 100,
          grace: '4%',
          border: {
            display: false
          },
          grid: {
            color: gridColor,
            drawBorder: false,
            lineWidth: 1
          },
          ticks: {
            callback(value) {
              return `${value}%`;
            },
            font: {
              size: 11,
              weight: '500'
            },
            color: tickColor,
            padding: 6,
            stepSize: 20
          }
        }
      },
      plugins: {
        legend: {
          position: 'bottom',
          align: 'center',
          labels: {
            usePointStyle: true,
            pointStyle: 'rectRounded',
            boxWidth: 10,
            boxHeight: 10,
            padding: 14,
            font: {
              size: 11,
              weight: '600'
            },
            color: tickColor
          }
        },
        title: {
          display: false
        },
        tooltip: {
          backgroundColor: tooltipBg,
          titleColor: tooltipText,
          bodyColor: tooltipMuted,
          borderColor: border,
          borderWidth: 1,
          cornerRadius: 8,
          padding: 10,
          displayColors: true,
          boxPadding: 4,
          callbacks: {
            label(context) {
              const value = context.raw || 0;
              return `${context.dataset.label}: ${value}%`;
            }
          }
        }
      },
      elements: {
        bar: {
          borderWidth: 0
        }
      },
      animation: {
        duration: 500,
        easing: 'easeOutQuart'
      }
    };
  }, [theme]);

  const handleViewChange = (e) => {
    setViewType(e.target.value);
  };

  const header = (
    <div className="sessions-score-header">
      <h3>
        Category distribution by user Score
        <span className="info-tooltip">
          <span className="info-icon">i</span>
          <span className="tooltip-text">{tooltipTexts.scoreDistribution}</span>
        </span>
      </h3>
      <select
        value={viewType}
        onChange={handleViewChange}
        className="view-selector"
        aria-label="Select score distribution view"
      >
        <option value="session">Session-wise</option>
        <option value="assessment">Assessment-wise</option>
      </select>
    </div>
  );

  if (isLoading) {
    return (
      <div className="sessions-score-chart chart-container">
        {header}
        <div className="loading-container">
          <div className="sessions-score-loader" aria-hidden="true" />
          Loading chart data...
        </div>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div className="sessions-score-chart chart-container">
        {header}
        <div className="loading-container">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="sessions-score-chart chart-container">
      {header}
      <div className="sessions-chart-canvas">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

SessionsChart.propTypes = {
  vehicleId: PropTypes.string
};

export default SessionsChart;
